import { useEffect, useState } from "react";
import { Smartphone, Share, Plus, X, Home } from "lucide-react";

const DISMISS_KEY = "grindstugatan_install_dismissed_at";
const DISMISS_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 dagar

const detectPlatform = () => {
  if (typeof window === "undefined") return "unknown";
  const ua = window.navigator.userAgent;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (standalone) return "installed";
  if (/iPhone|iPad|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
};

/**
 * InstallHint — DISKRET (ej popup) installations-tips som visas på Dashboard.
 * För iOS visas en knapp som öppnar ios-instruktions-modal när användaren KLICKAR.
 * För Android lyssnar vi på beforeinstallprompt och visar knapp som triggar nativ
 * install-prompt. Om appen redan är installerad → visas inget.
 */
export function InstallHint() {
  const [platform, setPlatform] = useState("unknown");
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    if (p === "installed") {
      setHidden(true);
      return;
    }
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) {
      setHidden(true);
      return;
    }

    const onBefore = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    const onInstalled = () => setHidden(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
    setShowIosHelp(false);
  };

  const clickInstall = async () => {
    if (platform === "ios") {
      setShowIosHelp(true);
      return;
    }
    if (deferredEvt) {
      try {
        deferredEvt.prompt();
        const { outcome } = await deferredEvt.userChoice;
        if (outcome === "accepted") setHidden(true);
        setDeferredEvt(null);
      } catch (_) {
        setShowIosHelp(true);
      }
    } else {
      // Fallback för Chrome som inte triggat eventet — visa iOS-style hjälp
      setShowIosHelp(true);
    }
  };

  // Visa bara om: inte installerad, inte dismissad, och antingen iOS eller har deferredEvt
  if (hidden) return null;
  if (platform !== "ios" && !deferredEvt && !showIosHelp) return null;

  return (
    <>
      <div
        className="mx-3 mb-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[color:var(--border)] p-3 flex items-center gap-3"
        data-testid="install-hint"
      >
        <div className="w-8 h-8 rounded-lg bg-[color:var(--moss)]/10 text-[color:var(--moss)] flex items-center justify-center flex-shrink-0">
          <Home size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium leading-snug">Lägg till på hemskärmen</p>
          <p className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5">
            Snabbare åtkomst & full skärm
          </p>
        </div>
        <button
          onClick={clickInstall}
          className="text-xs font-medium px-3 py-1.5 rounded-full bg-[color:var(--moss)] text-white flex-shrink-0"
          data-testid="install-hint-button"
        >
          Visa hur
        </button>
        <button
          onClick={dismiss}
          className="rounded-lg p-1 hover:bg-white/50 flex-shrink-0"
          data-testid="install-hint-close"
          aria-label="Stäng"
        >
          <X size={14} className="text-[color:var(--text-tertiary)]" />
        </button>
      </div>

      {showIosHelp && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-3"
          onClick={() => setShowIosHelp(false)}
          data-testid="install-ios-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-sm rounded-2xl shadow-xl p-5"
            data-testid="install-ios-modal"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#EEF2F7] text-[color:var(--moss)] flex items-center justify-center flex-shrink-0">
                <Smartphone size={18} />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg font-medium">Så här gör du</h3>
                <p className="text-sm text-[color:var(--text-secondary)] mt-0.5">
                  Tre steg i Safari-läsaren.
                </p>
              </div>
              <button
                onClick={() => setShowIosHelp(false)}
                className="rounded-lg p-1 hover:bg-[color:var(--bg-elevated)]"
                data-testid="install-ios-close"
                aria-label="Stäng"
              >
                <X size={18} />
              </button>
            </div>

            <ol className="space-y-3 mt-4">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[color:var(--moss)] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
                  1
                </span>
                <p className="text-sm text-[color:var(--text-secondary)] leading-snug">
                  Tryck på <Share size={14} className="inline -mt-0.5 text-[color:var(--jarvis)]" />{" "}
                  <span className="font-medium">Dela</span>-knappen längst ner i Safari.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[color:var(--moss)] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
                  2
                </span>
                <p className="text-sm text-[color:var(--text-secondary)] leading-snug">
                  Skrolla ner och välj{" "}
                  <Plus size={14} className="inline -mt-0.5 text-[color:var(--jarvis)]" />{" "}
                  <span className="font-medium">Lägg till på hemskärmen</span>.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[color:var(--moss)] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
                  3
                </span>
                <p className="text-sm text-[color:var(--text-secondary)] leading-snug">
                  Tryck <span className="font-medium">Lägg till</span> uppe i högra hörnet — klart!
                </p>
              </li>
            </ol>

            <button
              onClick={dismiss}
              className="mt-5 w-full h-11 rounded-xl bg-[color:var(--moss)] text-white text-sm font-medium hover:bg-[#1f421f]"
              data-testid="install-ios-done"
            >
              Tack, jag fixar det
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallHint;
